import type { ReactElement } from 'react';

import { formatDateTime } from '@/components/vacancy-complaints/Field';
import {
    COMPLAINT_REASON_LABEL,
    COMPLAINT_STATUS_COLOR,
    COMPLAINT_STATUS_LABEL
} from '@/lib/vacancyComplaintLabels';
import type { OtherVacancyComplaintRef } from '@/lib/vacancyComplaintsSchemas';

interface OtherComplaintsSectionProps {
    count: number;
    items: OtherVacancyComplaintRef[];
}

/**
 * Блок «Другие жалобы на эту вакансию» детали (design-spec §2.5): счётчик +
 * список (дата · причина · статус), без текущей жалобы (фильтрует backend).
 */
export function OtherComplaintsSection({
    count,
    items
}: OtherComplaintsSectionProps): ReactElement {
    return (
        <section
            className="rounded border border-gray-200 bg-gray-50 p-4"
            data-testid="vacancy-complaint-others"
        >
            <h2 className="mb-3 text-base font-semibold text-gray-900">
                Другие жалобы на эту вакансию
            </h2>
            {count === 0 ? (
                <p
                    className="text-sm text-gray-500"
                    data-testid="vacancy-complaint-others-empty"
                >
                    Других жалоб на эту вакансию нет.
                </p>
            ) : (
                <>
                    <p className="mb-2 text-sm text-gray-700">Всего: {count}</p>
                    <ul className="space-y-1 text-sm">
                        {items.map((item) => (
                            <li
                                key={item.id}
                                className="flex flex-wrap items-center gap-2 text-gray-700"
                            >
                                <span className="text-xs text-gray-500">
                                    {formatDateTime(item.createdAt)}
                                </span>
                                <span>{COMPLAINT_REASON_LABEL[item.reason]}</span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${COMPLAINT_STATUS_COLOR[item.status]}`}
                                >
                                    {COMPLAINT_STATUS_LABEL[item.status]}
                                </span>
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </section>
    );
}
